import { buildStreakEventsFromData, computeStreaks } from './streaks';
import { buildFrequentPlaceInputs, computeFrequentPlaces } from './frequentPlaces';
import {
  expandAggregateDays,
  isMediaAggregate,
  mediaTotal,
} from './mediaAggregates';

export const uniqueCities = (data = {}) => {
  const cities = new Map();

  (data.checkins || []).forEach(c => {
    if (c.city) {
      const key = c.city.trim().toLowerCase();
      if (!cities.has(key)) {
        cities.set(key, { city: c.city.trim(), country: c.country || '', count: 0 });
      }
      cities.get(key).count += 1;
    }
  });

  (data.concerts || []).forEach(c => {
    if (c.city_name) {
      const key = c.city_name.trim().toLowerCase();
      if (!cities.has(key)) {
        cities.set(key, { city: c.city_name.trim(), country: c.country_name || '', count: 0 });
      }
      cities.get(key).count += 1;
    }
  });

  return [...cities.values()].sort((a, b) => b.count - a.count || a.city.localeCompare(b.city));
};

export const countUniquePlaces = (data = {}) => {
  const places = new Set();

  (data.checkins || []).forEach((checkin) => {
    const key = checkin.venue_id || checkin.venue_name || checkin.venue || checkin.name;
    if (key) places.add(String(key).trim().toLowerCase());
  });

  return places.size;
};

export const routeDistanceByType = (data = {}) => {
  const byType = {};

  (data.health || []).forEach(route => {
    const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
    const dist = Number(firstPoint.distance) || 0;
    byType.health = (byType.health || 0) + dist / 1000;
  });

  (data.moves || []).forEach(route => {
    const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
    const routeType = firstPoint.route_type || 'walking';
    const dist = Number(firstPoint.distance) || 0;
    byType[routeType] = (byType[routeType] || 0) + dist / 1000;
  });

  (data.googleMaps || []).forEach(route => {
    const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
    const dist = Number(firstPoint.distance) || 0;
    byType.google_maps = (byType.google_maps || 0) + dist / 1000;
  });

  return byType;
};

export const routeDurationByType = (data = {}) => {
  const byType = {};

  (data.health || []).forEach(route => {
    const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
    const dur = Number(firstPoint.duration) || 0;
    byType.health = (byType.health || 0) + dur / 60;
  });

  return byType;
};

export const routeSessionCounts = (data = {}) => {
  const counts = {};

  (data.health || []).forEach(() => { counts.health = (counts.health || 0) + 1; });

  (data.moves || []).forEach(route => {
    const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
    const routeType = firstPoint.route_type || 'walking';
    counts[routeType] = (counts[routeType] || 0) + 1;
  });

  (data.googleMaps || []).forEach(() => { counts.google_maps = (counts.google_maps || 0) + 1; });

  return counts;
};

const extractDate = (item) => {
  const candidates = [item.date, item.entry_date, item.watched_at, item.event_date, item.played_at, item.route_date];
  for (const d of candidates) {
    if (d) return String(d).slice(0, 10);
  }
  return null;
};

const extractDateFromRoute = (route) => {
  const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
  const d = firstPoint.route_date || firstPoint.date;
  return d ? String(d).slice(0, 10) : null;
};

export const mostActiveDays = (data = {}, { topN = 10 } = {}) => {
  const dayCounts = new Map();

  const addItemDays = (items) => {
    expandAggregateDays(items, 'date').forEach((item) => {
      const day = extractDate(item);
      if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
    });
  };

  (data.checkins || []).forEach(c => {
    const day = extractDate(c);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  (data.health || []).forEach(route => {
    const day = extractDateFromRoute(route);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  (data.moves || []).forEach(route => {
    const day = extractDateFromRoute(route);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  (data.googleMaps || []).forEach(route => {
    const day = extractDateFromRoute(route);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  addItemDays(data.movies);
  addItemDays(data.shows);

  (data.diary || []).forEach(d => {
    const day = extractDate(d);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  (data.concerts || []).forEach(c => {
    const day = extractDate(c);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  addItemDays(data.spotify);
  addItemDays(data.appleMusic);

  return [...dayCounts.entries()]
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => b.count - a.count || a.date.localeCompare(b.date))
    .slice(0, topN);
};

export const topVenues = (data = {}, { topN = 10 } = {}) => (
  computeFrequentPlaces(buildFrequentPlaceInputs(data), { topN })
);

export const musicStats = (data = {}) => {
  // Combined aggregate from /map-data (exact parity with raw musicStats).
  if (isMediaAggregate(data.music)) {
    const days = new Set((data.music.byDay || []).map((d) => d.date).filter(Boolean));
    return {
      totalPlays: data.music.total || 0,
      uniqueArtists: data.music.uniqueArtists || 0,
      uniqueTracks: data.music.uniqueTracks || 0,
      daysWithMusic: days.size,
      spotifyPlays: mediaTotal(data.spotify),
      appleMusicPlays: mediaTotal(data.appleMusic),
      topArtists: [...(data.music.topArtists || [])],
      topTracks: [...(data.music.topTracks || [])],
    };
  }

  const allPlays = [...(data.spotify || []), ...(data.appleMusic || [])]
    .filter((p) => p && typeof p === 'object' && !p._aggregate);

  const artists = new Map();
  const tracks = new Map();
  const days = new Set();

  allPlays.forEach(p => {
    const day = extractDate(p);
    if (day) days.add(day);

    if (p.artist_name) {
      const key = p.artist_name.trim().toLowerCase();
      artists.set(key, { artist: p.artist_name.trim(), count: (artists.get(key)?.count || 0) + 1 });
    }

    if (p.track_name) {
      const key = p.track_name.trim().toLowerCase();
      tracks.set(key, { track: p.track_name.trim(), count: (tracks.get(key)?.count || 0) + 1 });
    }
  });

  const topArtists = [...artists.values()]
    .sort((a, b) => b.count - a.count || a.artist.localeCompare(b.artist))
    .slice(0, 10);

  const topTracks = [...tracks.values()]
    .sort((a, b) => b.count - a.count || a.track.localeCompare(b.track))
    .slice(0, 10);

  return {
    totalPlays: allPlays.length,
    uniqueArtists: artists.size,
    uniqueTracks: tracks.size,
    daysWithMusic: days.size,
    spotifyPlays: Array.isArray(data.spotify) ? data.spotify.length : mediaTotal(data.spotify),
    appleMusicPlays: Array.isArray(data.appleMusic) ? data.appleMusic.length : mediaTotal(data.appleMusic),
    topArtists,
    topTracks,
  };
};

export const entertainmentStats = (data = {}) => {
  if (isMediaAggregate(data.movies) || isMediaAggregate(data.shows)) {
    const movies = isMediaAggregate(data.movies) ? data.movies : null;
    const shows = isMediaAggregate(data.shows) ? data.shows : null;
    const movieList = movies ? [] : (data.movies || []).filter(m => m.item_type === 'movie' || m.watched_at);
    const episodeList = shows ? [] : (data.shows || []).filter(s => s.item_type === 'episode' || s.watched_at);

    let topShows = shows ? [...(shows.topShows || [])] : null;
    if (!topShows) {
      const showMap = new Map();
      episodeList.forEach(e => {
        if (e.show_title) {
          const key = e.show_title.trim().toLowerCase();
          showMap.set(key, {
            show: e.show_title.trim(),
            episodes: (showMap.get(key)?.episodes || 0) + 1,
            year: e.year || '',
          });
        }
      });
      topShows = [...showMap.values()]
        .sort((a, b) => b.episodes - a.episodes || a.show.localeCompare(b.show))
        .slice(0, 10);
    }

    return {
      totalMovies: movies ? (movies.total || 0) : movieList.length,
      totalEpisodes: shows ? (shows.total || 0) : episodeList.length,
      uniqueShows: shows ? (shows.uniqueShows || 0) : new Set(
        episodeList.filter(e => e.show_title).map(e => e.show_title.trim().toLowerCase())
      ).size,
      topShows,
    };
  }

  const movies = (data.movies || []).filter(m => m.item_type === 'movie' || m.watched_at);
  const episodes = (data.shows || []).filter(s => s.item_type === 'episode' || s.watched_at);

  const shows = new Map();
  episodes.forEach(e => {
    if (e.show_title) {
      const key = e.show_title.trim().toLowerCase();
      shows.set(key, {
        show: e.show_title.trim(),
        episodes: (shows.get(key)?.episodes || 0) + 1,
        year: e.year || '',
      });
    }
  });

  const topShows = [...shows.values()]
    .sort((a, b) => b.episodes - a.episodes || a.show.localeCompare(b.show))
    .slice(0, 10);

  return {
    totalMovies: movies.length,
    totalEpisodes: episodes.length,
    uniqueShows: shows.size,
    topShows,
  };
};

export const concertStats = (concerts = []) => {
  const artists = new Map();
  const cities = new Map();

  (concerts || []).forEach(c => {
    if (c.artist_name) {
      const key = c.artist_name.trim().toLowerCase();
      artists.set(key, { artist: c.artist_name.trim(), count: (artists.get(key)?.count || 0) + 1 });
    }
    if (c.city_name) {
      const key = c.city_name.trim().toLowerCase();
      cities.set(key, { city: c.city_name.trim(), count: (cities.get(key)?.count || 0) + 1 });
    }
  });

  const topArtists = [...artists.values()]
    .sort((a, b) => b.count - a.count || a.artist.localeCompare(b.artist))
    .slice(0, 10);

  const topCities = [...cities.values()]
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city))
    .slice(0, 10);

  return {
    totalConcerts: concerts.length,
    uniqueArtists: artists.size,
    uniqueCities: cities.size,
    topArtists,
    topCities,
  };
};

export const activeStreak = (data = {}) => {
  const days = new Set();

  const addDays = (items, fn) => {
    (items || []).forEach(item => {
      const day = fn(item);
      if (day) days.add(day);
    });
  };

  addDays(data.checkins, extractDate);
  addDays(data.health, extractDateFromRoute);
  addDays(data.moves, extractDateFromRoute);
  addDays(data.googleMaps, extractDateFromRoute);
  addDays(expandAggregateDays(data.movies, 'watched_at'), extractDate);
  addDays(expandAggregateDays(data.shows, 'watched_at'), extractDate);
  addDays(data.diary, extractDate);
  addDays(data.concerts, extractDate);
  addDays(expandAggregateDays(data.spotify, 'played_at'), extractDate);
  addDays(expandAggregateDays(data.appleMusic, 'played_at'), extractDate);

  const sorted = [...days].sort();
  if (sorted.length === 0) return { longest: 0, current: 0, totalDays: 0 };

  let longest = 1;
  let current = 1;
  let totalDays = sorted.length;

  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    const curr = new Date(sorted[i]);
    const diff = (curr - prev) / (1000 * 60 * 60 * 24);

    if (diff === 1) {
      current++;
      longest = Math.max(longest, current);
    } else {
      current = 1;
    }
  }

  const lastDate = sorted[sorted.length - 1];
  const today = new Date().toISOString().slice(0, 10);
  const daysSinceLast = Math.floor((new Date(today) - new Date(lastDate)) / (1000 * 60 * 60 * 24));

  return {
    longest,
    current: daysSinceLast <= 1 ? current : 0,
    totalDays,
  };
};

export const streaksByType = (data = {}) => computeStreaks(
  buildStreakEventsFromData(data),
  ['music', 'diary', 'checkin', 'route', 'any']
);

export const computeAllInsights = (data = {}) => ({
  cities: uniqueCities(data),
  distanceByType: routeDistanceByType(data),
  durationByType: routeDurationByType(data),
  sessionCounts: routeSessionCounts(data),
  mostActiveDays: mostActiveDays(data),
  topVenues: topVenues(data),
  music: musicStats(data),
  entertainment: entertainmentStats(data),
  concerts: concertStats(data.concerts),
  streak: activeStreak(data),
  streaksByType: streaksByType(data),
});
